import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Button, CircularProgress, Divider, Stack } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const loadBlogPosts = async (limit?: number) => {
  // Default to all posts for pagination
  const postLimit = limit || 100;
  const apiUrl = `https://technology-for-intelligent-decisions.ghost.io/ghost/api/content/posts/?key=504e7dbb1cb83bfabd492074ef&limit=${postLimit}`;
  
  try {
    console.log('Fetching blog posts from:', apiUrl);
    const response = await fetch(apiUrl, {
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Blog posts loaded:', data.posts?.length || 0);
    return data.posts || [];
  } catch (error) {
    console.error('Error loading blog posts:', error);
    return [];
  }
};

export default function BlogPostsPage() {
  const [blogPosts, setBlogPosts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const BLOGS_PER_PAGE = 6;
  const [showAddtlBlogs, setShowAddtlBlogs] = React.useState(0);

  React.useEffect(() => {
    loadBlogPosts(100).then(posts => {
      if (posts.length === 0) {
        setError('Unable to load blog posts. Please check the console for details.');
      }
      setBlogPosts(posts);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load blog posts:', err);
      setError('Failed to load blog posts. Please try again later.');
      setLoading(false);
    });
  }, []);

  return (
    <Stack spacing={2}>
      <Typography component="h3" variant="h3" color="text.primary" sx={{ pb: 2 }}>
        Blog Posts
      </Typography>
      <Divider />
      {loading && <CircularProgress />}
      {error && !loading && (
        <Typography color="error" variant="body1">
          {error}
        </Typography>
      )}
      <Grid container spacing={2}>
        {blogPosts.slice(0, (showAddtlBlogs + 1) * BLOGS_PER_PAGE).map((post) => (
          <Grid item xs={12} sm={6} key={post.id} sx={{ display: 'flex' }}>
            <Card
              component="a"
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                textDecoration: 'none',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
            >
              {post.feature_image && (
                <CardMedia
                  component="img"
                  height="200"
                  image={post.feature_image}
                  alt={post.title}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent>
                <Typography variant="h6" component="h4" gutterBottom>
                  {post.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {post.excerpt || post.custom_excerpt}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(post.published_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {post.reading_time} min read
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {(blogPosts.length > ((showAddtlBlogs + 1) * BLOGS_PER_PAGE)) ? 
        <Button onClick={() => setShowAddtlBlogs(showAddtlBlogs + 1)} startIcon={<ExpandMoreIcon/>}>Show more blogs</Button> : ''}
      {(showAddtlBlogs > 0) ? 
        <Button onClick={() => setShowAddtlBlogs(0)} startIcon={<ExpandLessIcon/>}>Hide</Button> : ''}
    </Stack>
  );
}
